import React from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import * as R from 'ramda';

import {prettyNumber, numberWithCommas} from 'js/common/string_helper';
import * as ObjectHelper from 'js/common/object_helper';
import {
    CAMPAIGN_PRICING_MODELS,
    VIDEO_OR_IMAGE_CAMPAIGN,
    DEFAULT_PRICING_MODEL,
    DefaultInterests
} from 'js/common/campaign_helper';

const {CampaignAudienceAgeEnum, CampaignAudienceGender} = window.sn.enums;

const PROGRESS_BARS_PROCESSORS = [
    {
        property: 'audience_gender',
        fn: gender => {
            if (gender === CampaignAudienceGender.ALL) {
                return {
                    targeting: 1.0,
                    ticks: 0
                };
            }
            return {
                targeting: 0.95,
                ticks: 0
            };
        }
    },
    {
        property: 'audience_age',
        fn: ages => {
            const agesArr = R.split(',', ages);
            if (
                agesArr.length > 1 ||
                (agesArr.length === 1 && agesArr[0] === CampaignAudienceAgeEnum.ALL)
            ) {
                return {
                    targeting: 1.0,
                    ticks: 0
                };
            }
            return {
                targeting: 0.85,
                ticks: 1,
                reason: 'Selecting less than 2 age groups increases costs'
            };
        }
    },
    {
        property: 'audience_location_custom',
        fn: customLocations => {
            if (customLocations) {
                return {
                    targeting: 0.5,
                    ticks: 1,
                    reason: 'Selecting custom locations increases costs'
                };
            }
            return {
                targeting: 1.0,
                ticks: 0
            };
        }
    },
    {
        property: 'audience_interests',
        fn: (() => {
            const defaultInterestsMap = R.reduce(
                (acc, defaultValue) => {
                    acc[defaultValue.toLowerCase()] = true;
                    return acc;
                },
                {},
                DefaultInterests
            );

            const hasCustomInterests = R.pipe(
                R.filter(val => !defaultInterestsMap[val.toLowerCase()]),
                customInterests => customInterests.length > 0
            );

            const hasDefaultInterests = R.pipe(
                R.filter(val => defaultInterestsMap[val.toLowerCase()]),
                defaultInterests => defaultInterests.length > 0
            );

            return interests => {
                const interestsArr = !interests ? [] : interests.split(',');
                if (
                    interestsArr.length > 0 &&
                    R.complement(hasDefaultInterests)(interestsArr) &&
                    hasCustomInterests(interestsArr)
                ) {
                    return {
                        targeting: 0.5,
                        ticks: 1,
                        reason:
                            'Selecting custom interests without selecting any default interests increases costs'
                    };
                }
                return {
                    targeting: 1.0,
                    ticks: 0
                };
            };
        })()
    },
    {
        property: 'audience_languages',
        fn: languages => {
            if (!languages || languages.toLowerCase() === 'english') {
                return {
                    targeting: 1.0,
                    ticks: 0
                };
            }
            return {
                targeting: 0.85,
                ticks: 1,
                reason: 'Selecting languages other than "English" increases costs'
            };
        }
    }
];

const getPricingModelForCampaign = campaign => {
    const pricingModel =
        CAMPAIGN_PRICING_MODELS[VIDEO_OR_IMAGE_CAMPAIGN[campaign.campaign_display_type]][
            campaign.objective
        ];
    if (pricingModel === undefined) {
        return DEFAULT_PRICING_MODEL;
    }
    return pricingModel;
};

const BidDetails = ({enums, campaign, warnSaasCampaign = false}) => {
    const {
        budget,
        bid,
        bid_type,
        io_signed,
        required_engagements,
        required_pieces_of_content,
        required_reach,
        is_saas,
        objective,
        addons,
        followersTiersCount,
        recommended_bid
    } = campaign;

    const totalCountOfRequiredContentFromTiers =
        followersTiersCount.reduce((acc, item) => acc + (item?.count ?? 0), 0) || 'N/A';

    const {CampaignObjective, BidType} = enums;

    const calculateCustomizationLevels = () => {
        const MAX_LEVELS = 3;

        return R.pipe(
            R.map(processor => processor.fn(campaign[processor.property])),
            R.map(R.prop('ticks')),
            R.map(ticks => (ticks === undefined ? 0 : ticks)),
            R.sum,
            ticks => (ticks < MAX_LEVELS ? ticks : MAX_LEVELS)
        )(PROGRESS_BARS_PROCESSORS);
    };

    const calculateEstimatedEngagement = () => {
        const customizationLevels = calculateCustomizationLevels();
        const pricingModel = getPricingModelForCampaign(campaign).engagements_or_content_created;
        const actual = campaign.budget / pricingModel[customizationLevels];

        return actual;
    };

    const bidTypeValue = ObjectHelper.propOr('N/A', bid_type, BidType);
    const requiredEngagementsValue = required_engagements || 'N/A';
    const requiredPiecesOfContentValue = required_pieces_of_content || 'N/A';
    const requiredReachValue = required_reach || 'N/A';

    const shouldDisplaySaasWarning = warnSaasCampaign && is_saas;
    const saasWarning = (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id="tooltip">
                    {`SAAS Campaigns have default rev share set to 1
                        and will use objective bid to calculate payouts on CPCC campaigns
                        You can edit this in the campaign config`}
                </Tooltip>
            }
            rootClose
        >
            <span className="label label-danger">SAAS CAMPAIGN</span>
        </OverlayTrigger>
    );

    const estimatedEngValue =
        objective === CampaignObjective.SOCIAL_ENGAGEMENT
            ? prettyNumber(budget / bid, 0)
            : prettyNumber(calculateEstimatedEngagement(), 0);

    return (
        <div className="bid-details">
            <div className="section-title">Bid {shouldDisplaySaasWarning ? saasWarning : null}</div>
            <div className="bid-details-grid">
                <div className="bid-detail">
                    <div className="section-details-title">Budget</div>
                    <div className="section-details-value">{`$${numberWithCommas(budget)}`}</div>
                </div>
                <div className="bid-detail">
                    <div className="section-details-title">Bid Type</div>
                    <div className="section-details-value">{bidTypeValue}</div>
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Custom Add-ons</div>
                    {addons?.length ? (
                        addons.map(addon => (
                            <div className="bid-detail" key={addon.id}>
                                <div className="section-details-subtitle">{addon.label}</div>
                                <div className="section-details-value">{`$${numberWithCommas(
                                    addon.price
                                )}`}</div>
                            </div>
                        ))
                    ) : (
                        <div className="section-details-value">N/A</div>
                    )}
                </div>
                <div className="bid-detail">
                    <div className="section-details-title">Recommended Bid</div>
                    <div className="section-details-value">{`$${
                        recommended_bid === null ? 0 : recommended_bid
                    }`}</div>
                </div>
                <div className="bid-detail">
                    <div className="section-details-title">IO Signed</div>
                    <div className="section-details-value">{io_signed === 1 ? 'Yes' : 'No'}</div>
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Targeting x Content Count</div>
                    {followersTiersCount?.length ? (
                        followersTiersCount.map(item => (
                            <div className="section-details-item" key={item.id}>
                                <div className="section-details-value">
                                    {item.label + ` (${item.description})`}
                                </div>
                                <div className="section-details-value">{item.count}</div>
                            </div>
                        ))
                    ) : (
                        <div className="section-details-value">N/A</div>
                    )}
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Required Pieces of Content</div>
                    <div className="section-details-value">
                        {totalCountOfRequiredContentFromTiers}
                    </div>
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Estimated Eng.</div>
                    <div className="section-details-value">{estimatedEngValue}</div>
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Required Engagement</div>
                    <div className="section-details-value">{requiredEngagementsValue}</div>
                </div>
                <div className="bid-detail full-width">
                    <div className="section-details-title">Required Reach</div>
                    <div className="section-details-value">{requiredReachValue}</div>
                </div>
            </div>
        </div>
    );
};

BidDetails.propTypes = {};

export default BidDetails;



// WEBPACK FOOTER //
// ./components/verify_receipt_common/bid_details.js